import axios from 'axios'
import React, { useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { CreateVendorEnd } from '../../../constants/api.constants'
import Toast from '../../../utils/Toast/Toast'
import { emailRegex } from '../../../constants/regex'
import ReactPasswordChecklist from 'react-password-checklist'
import HideShowToggle from '../../HideShowToggle/HideShowToggle'
import {
  lowerCaseRegex,
  minLengthRegex,
  upperCaseRegex,
  numberRegex,
  specialCharRegex,
  maxLengthRegex,
  NAME_REGEX,
} from '../../../constants/regex'

const NewVendorModal = ({ show, handleClose, loadAllVendors }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [passVisibility, setPassVisibility] = useState(false)
  const [passVisibility1, setPassVisibility1] = useState(false)
  const [passRetype, setPassReType] = useState('')

  const [validations, setValidations] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasMinLength: false,
    hasSpecialChar: false,
    hasMaxChar: false,
  })

  const [data, setData] = useState({
    name: '',
    email: '',
    password: '',
    expiry_date: '',
  })

  const validatePassword = (newPass) => {
    const hasLowerCase = lowerCaseRegex.test(newPass)
    const hasUpperCase = upperCaseRegex.test(newPass)
    const hasNumber = numberRegex.test(newPass)
    const hasMinLength = minLengthRegex.test(newPass)
    const hasSpecialChar = specialCharRegex.test(newPass)
    const hasMaxChar = maxLengthRegex.test(newPass)
    setValidations({
      hasLowerCase,
      hasUpperCase,
      hasNumber,
      hasMinLength,
      hasSpecialChar,
      hasMaxChar,
    })
  }

  const handleModalClose = () => {
    handleClose()
    setData({
      name: '',
      email: '',
      password: '',
      expiry_date: '',
    })
    setPassReType('')
    setValidations({
      hasLowerCase: false,
      hasUpperCase: false,
      hasNumber: false,
      hasMinLength: false,
      hasSpecialChar: false,
      hasMaxChar: false,
    })
  }

  const handleSubmit = async () => {
    try {
      setIsLoading(true)

      if (data.name === '') {
        Toast('err', 'Please enter vendor name')
        setIsLoading(false)
        return
      }

      if (data.name.length < 3) {
        Toast('err', 'Name must be at least 3 characters long.')
        setIsLoading(false)
        return
      }

      if (data.email === '') {
        Toast('err', 'Please enter email')
        setIsLoading(false)
        return
      }

      if (!emailRegex.test(data.email)) {
        Toast('err', 'Please enter a valid email')
        setIsLoading(false)
        return
      }

      if (
        !validations?.hasLowerCase ||
        !validations?.hasNumber ||
        !validations?.hasUpperCase ||
        !validations?.hasSpecialChar
      ) {
        Toast('err', 'Password does not meet requirements')
        setIsLoading(false)
        return
      }

      if (data.password !== passRetype) {
        Toast('err', 'Passwords do not match')
        setIsLoading(false)
        return
      }

      if (data.password.length < 10 || data.password.length >= 32) {
        Toast('err', 'Password must be between 10-32 characters')
        setIsLoading(false)
        return
      }

      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        expiry_date: data.expiry_date || null, // null means perpetual access
      }

      const response = await axios.post(CreateVendorEnd, payload, {
        headers: {
          menuboard: localStorage.getItem('menu_token'),
        },
      })

      if (response.status === 200) {
        Toast('success', 'Vendor created successfully!')
        handleModalClose()
        setIsLoading(false)
        loadAllVendors()
      } else {
        throw new Error(
          response?.data?.msg || 'Something went wrong! Try again later.'
        )
      }
    } catch (error) {
      setIsLoading(false)
      Toast(
        'err',
        error.response?.data?.msg || 'Something went wrong! Try again later.'
      )
    }
  }

  return (
    <Modal show={show} onHide={handleModalClose}>
      <Modal.Header closeButton style={{ border: 'none' }}>
        <Modal.Title>Add New Vendor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='plain-input me-2'>
          <label htmlFor=''>Vendor Name*</label>
          <br />
          <input
            type='text'
            placeholder='Enter vendor name'
            value={data?.name}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(NAME_REGEX, '')
              setData({ ...data, name: sanitizedValue })
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
        </div>

        <div className='plain-input me-2 mt-2'>
          <label htmlFor=''>Email*</label>
          <br />
          <input
            type='email'
            autoComplete='new-password'
            placeholder='vendor@example.com'
            value={data?.email}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(
                /[^a-zA-Z0-9\s@.]/g,
                ''
              )
              setData({ ...data, email: sanitizedValue })
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
        </div>

        <div className='plain-input me-2 mt-2'>
          <label htmlFor=''>
            Access Expiry Date
            <small className='text-muted ms-2'>
              (Leave empty for perpetual/forever access)
            </small>
          </label>
          <br />
          <input
            type='date'
            value={data?.expiry_date}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setData({ ...data, expiry_date: e.target.value })}
          />
          <small className='d-block text-muted mt-1'>
            {data.expiry_date
              ? `Access will expire on ${new Date(data.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
              : 'Perpetual access - vendor will have access until manually deactivated'}
          </small>
        </div>

        <div className='plain-input me-2 mt-3'>
          <label htmlFor=''>Password*</label>
          <br />
          <input
            type={passVisibility ? 'text' : 'password'}
            placeholder='Minimum length 10'
            onChange={(e) => {
              setData({ ...data, password: e.target.value })
            }}
            autoComplete='new-password'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
          <HideShowToggle
            passVisibility={passVisibility}
            setPassVisibility={() => setPassVisibility(!passVisibility)}
          />
        </div>

        <div className='plain-input me-2 mt-2'>
          <label htmlFor=''>Re-Type Password*</label>
          <br />
          <input
            type={passVisibility1 ? 'text' : 'password'}
            placeholder='Re-type your password'
            onChange={(e) => {
              setPassReType(e.target.value)
              validatePassword(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
          <HideShowToggle
            passVisibility={passVisibility1}
            setPassVisibility={() => setPassVisibility1(!passVisibility1)}
          />
        </div>

        <ReactPasswordChecklist
          style={{ marginTop: '10px' }}
          rules={[
            'minLength',
            'maxLength',
            'specialChar',
            'number',
            'capital',
            'lowercase',
            'match',
          ]}
          minLength={10}
          maxLength={32}
          value={data?.password}
          valueAgain={passRetype}
          messages={{
            minLength: 'Password length minimum 10 characters',
            maxLength: 'Password length maximum 32 characters',
            specialChar: 'Password has special character.',
            number: 'Password has number.',
            capital: 'Password has a capital letter',
            lowercase: 'Password has a lowercase letter',
            match: 'Password does not match',
          }}
        />
      </Modal.Body>
      <Modal.Footer style={{ border: 'none' }}>
        <button className='primary-btn-light' onClick={handleModalClose}>
          Cancel
        </button>
        <button
          className='primary-btn d-flex justify-content-between align-items-center'
          onClick={handleSubmit}
        >
          Add Vendor
          {isLoading && (
            <Spinner className='ms-2' animation='border' size='sm' />
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default NewVendorModal
